import '../../styles/restScreens.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import LiveTripMap from './LiveTripMap';
import {
    buildClientTripView,
    formatPositionAge,
    isPositionStale,
} from '../../lib/liveTracking';

/**
 * LiveTripTrackingPanel — BL-187 Gate 4, Module 9 Trip Detail's live view.
 *
 * Ground truth: IntelAgent_BL187_GPS_LiveTracking_Build_v1_01.md, Gate 4 —
 * the tracking dot "plus a live ETA readout next to it, sourced from Gate 3",
 * matching Module 11 Gate 6's client-tier matrix: location + ETA + driver
 * identity only.
 *
 * Two streams, deliberately separate:
 *
 *   Position  arrives over Supabase Realtime on driver_locations, so the dot
 *             moves the moment a ping lands. Free.
 *   ETA       comes from /api/tracking/eta, which spends a Mapbox Directions
 *             request. Metered, so it is throttled independently of the pings.
 *
 * Coupling them would mean a Directions request per ping — roughly 240 an hour
 * per driver for a number that barely changes.
 */

// The ETA refresh floor. Matches ETA_MIN_INTERVAL_MS in server/mapboxRouting.js;
// the server governs the real spend, this just avoids asking pointlessly.
const ETA_REFRESH_MS = 30000;

function TrackingRow({ label, value, emphasis = false }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-wide text-[var(--ink-muted)]">{label}</div>
            <div className={`mt-0.5 ${emphasis ? 'text-2xl font-bold text-[var(--ink)]' : 'text-sm text-[var(--ink)]'}`}>
                {value}
            </div>
        </div>
    );
}

function LiveTripTrackingPanel({ assignmentId, driver = null, theme = 'light' }) {
    const [position, setPosition] = useState(null);
    const [eta, setEta] = useState(null);
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    // Re-render the "updated N min ago" caption on a timer; the underlying
    // position has not changed, but its age has.
    const [, setAgeTick] = useState(0);

    const lastEtaAtRef = useRef(0);

    const fetchEta = useCallback(async (force = false) => {
        if (!assignmentId) return;
        if (!force && Date.now() - lastEtaAtRef.current < ETA_REFRESH_MS) return;
        lastEtaAtRef.current = Date.now();

        try {
            const { data: { session } = {} } = await supabaseClient.auth.getSession();
            if (!session?.access_token) return;

            const response = await fetch(
                `/api/tracking/eta?assignmentId=${encodeURIComponent(assignmentId)}`,
                { headers: { Authorization: `Bearer ${session.access_token}` } }
            );
            const body = await response.json().catch(() => ({}));

            if (!response.ok) {
                setError(body.error === 'mapbox_not_configured'
                    ? 'Live tracking is not configured for this environment.'
                    : '');
                return;
            }

            setReason(body.reason || '');
            setEta(body.eta || null);
            // The route's position is authoritative on first load; Realtime takes
            // over from there.
            if (body.origin) {
                setPosition((current) => current || {
                    latitude: body.origin.latitude,
                    longitude: body.origin.longitude,
                    heading: body.origin.heading,
                    recordedAt: body.origin.recordedAt,
                });
            }
            setError('');
        } catch {
            // A failed refresh leaves the last known ETA on screen rather than
            // blanking it — a slightly old number beats no number.
        } finally {
            setLoading(false);
        }
    }, [assignmentId]);

    useEffect(() => {
        setLoading(true);
        fetchEta(true);
    }, [fetchEta]);

    // Live position — Realtime on driver_locations (BL-187 Gate 1 put the table
    // in the supabase_realtime publication for exactly this).
    useEffect(() => {
        if (!driver?.id || typeof supabaseClient.channel !== 'function') return undefined;

        const channel = supabaseClient.channel(`driver-locations-${driver.id}`, {
            config: { broadcast: { self: false } },
        });
        if (!channel || typeof channel.on !== 'function') return undefined;

        channel
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'driver_locations',
                filter: `driver_id=eq.${driver.id}`,
            }, (payload) => {
                const row = payload?.new;
                if (!row) return;
                setPosition({
                    latitude: Number(row.latitude),
                    longitude: Number(row.longitude),
                    heading: row.heading == null ? null : Number(row.heading),
                    recordedAt: row.recorded_at,
                });
                // A new ping may justify a new ETA — fetchEta's own floor decides.
                fetchEta();
            })
            .subscribe();

        return () => {
            if (typeof supabaseClient.removeChannel === 'function') {
                supabaseClient.removeChannel(channel);
            }
        };
    }, [driver?.id, fetchEta]);

    // Keep the ETA honest while a trip is live, even if no ping arrives.
    useEffect(() => {
        const id = setInterval(() => fetchEta(), ETA_REFRESH_MS);
        return () => clearInterval(id);
    }, [fetchEta]);

    // Age caption ticks on its own so "Updated just now" does not sit there for
    // ten minutes.
    useEffect(() => {
        const id = setInterval(() => setAgeTick((n) => n + 1), 30000);
        return () => clearInterval(id);
    }, []);

    // Everything below renders from this view and nothing else. Module 11 Gate 6
    // locked the client tier to location + ETA + driver identity, and building
    // the view through this helper means a field outside that set cannot reach
    // the panel just because a caller passed a fuller object in.
    const view = buildClientTripView({ position, eta, driver });
    const stale = isPositionStale(view.position?.recordedAt);

    const cardCls = theme === 'dark' ? 'bg-gray-700/40 border-gray-700' : 'bg-gray-50 border-gray-200';

    return (
        <div data-m6-layout="tracking" data-m6-component="LiveTripTrackingPanel" className={`rounded-xl border p-5 ${cardCls}`}>
            <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--ink-muted)]">
                    Live Tracking
                </h3>
                {view.position && (
                    <span className={`text-xs ${stale ? 'text-[var(--ui-alert)]' : 'text-[var(--ink-muted)]'}`}>
                        {formatPositionAge(view.position.recordedAt)}
                    </span>
                )}
            </div>

            {error && <p className="mb-3 text-sm text-[var(--ink-muted)]">{error}</p>}

            <LiveTripMap
                position={view.position}
                routeGeometry={view.eta?.geometry || null}
                vehicleType={view.driver?.vehicleType || null}
                stale={stale}
                className="mb-4"
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {/* The ETA readout Gate 4 exists to add, sitting beside the dot. */}
                <TrackingRow
                    label="Arrives in"
                    value={view.eta?.etaText || (loading ? 'Checking…' : 'ETA unavailable')}
                    emphasis
                />
                <TrackingRow label="Distance" value={view.eta?.distanceText || '—'} />
                <TrackingRow
                    label="Driver"
                    value={view.driver?.displayName || 'Not yet assigned'}
                />
            </div>

            {view.driver?.vehicleDescription && (
                <p className="mt-3 text-sm text-[var(--ink-muted)]">
                    Look for: {view.driver.vehicleDescription}
                </p>
            )}

            {!view.position && !loading && (
                <p className="mt-3 text-sm text-[var(--ink-muted)]">
                    {reason === 'awaiting_first_ping'
                        ? 'Waiting for the driver to start sharing their location.'
                        : 'No live position yet for this trip.'}
                </p>
            )}

            {stale && view.position && (
                <p className="mt-3 text-sm text-[var(--ui-alert)]">
                    This position may be out of date — the driver’s phone has not reported recently.
                </p>
            )}
        </div>
    );
}

export default LiveTripTrackingPanel;
